import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { mercadoPagoOAuthService } from '../services/mercadoPagoOAuthService';

const MercadoPagoOAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState<string>('Processando autorização do Mercado Pago...');

  useEffect(() => {
    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (errorParam) {
        setStatus('error');
        setMessage(errorDescription || errorParam || 'Erro ao autorizar integração com Mercado Pago');
        setTimeout(() => navigate('/admin/integracoes/mercadopago'), 3000);
        return;
      }

      try {
        setMessage('Processando autorização do Mercado Pago...');
        const result = await mercadoPagoOAuthService.processCallback(code, state);
        
        setStatus('success');
        const sellerInfo = result.sellerId ? ` (ID: ${result.sellerId})` : '';
        setMessage(`Integração autorizada com sucesso${sellerInfo}!`);
        
        // Admin é sempre o mesmo (admin.betterdelivery.com.br)
        setTimeout(() => {
          navigate('/admin/integracoes/mercadopago');
        }, 2000);
      } catch (error: any) {
        setStatus('error');
        setMessage(error.message || 'Erro ao processar autorização do Mercado Pago');
        setTimeout(() => navigate('/admin/integracoes/mercadopago'), 3000);
      }
    };

    processCallback();
  }, [searchParams, navigate]);

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        {status === 'processing' && (
          <>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" gutterBottom>
              Processando...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {message}
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              Sucesso!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              {message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Você será redirecionado automaticamente...
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/admin/integracoes/mercadopago')}
              >
                Ir para Integrações
              </Button>
            </Box>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon color="error" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h5" gutterBottom color="error.main">
              Erro na Autorização
            </Typography>
            <Alert severity="error" sx={{ mt: 2, mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            <Typography variant="body2" color="text.secondary" paragraph>
              Você pode tentar novamente na página de integrações.
            </Typography>
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/admin/integracoes/mercadopago')}
              >
                Voltar para Integrações
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default MercadoPagoOAuthCallbackPage;
